import {
	type Caller,
	type CallerInstruction,
	type CallerInstructionMode,
	type CallerMemoryUpdate,
	type ModelUsageAudit,
	normalizeCallerMemory,
} from "@repo/common";
import { db, deleteCallerCascade, functions, httpsCallable } from "@repo/ui";
import {
	collection,
	doc,
	onSnapshot,
	orderBy,
	query,
} from "firebase/firestore";
import {
	AlertTriangle,
	ArrowLeft,
	BrainCircuit,
	History,
	Plus,
	Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";

function formatDate(value: unknown): string {
	if (!value) return "-";
	if (value instanceof Date) return value.toLocaleString();
	if (typeof value === "object" && value !== null) {
		const timestamp = value as {
			seconds?: number;
			toDate?: () => Date;
		};
		if (typeof timestamp.toDate === "function") {
			return timestamp.toDate().toLocaleString();
		}
		if (typeof timestamp.seconds === "number") {
			return new Date(timestamp.seconds * 1000).toLocaleString();
		}
	}
	const parsed = new Date(value as string | number);
	return Number.isNaN(parsed.getTime())
		? String(value)
		: parsed.toLocaleString();
}

function dateMs(value: unknown): number {
	if (!value) return 0;
	if (value instanceof Date) return value.getTime();
	if (typeof value === "object" && value !== null) {
		const timestamp = value as {
			seconds?: number;
			toDate?: () => Date;
		};
		if (typeof timestamp.toDate === "function") {
			return timestamp.toDate().getTime();
		}
		if (typeof timestamp.seconds === "number") {
			return timestamp.seconds * 1000;
		}
	}
	const parsed = new Date(value as string | number);
	return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function errorMessage(error: unknown) {
	return error instanceof Error ? error.message : String(error);
}

function instructionModeLabel(mode: CallerInstructionMode | string) {
	switch (mode) {
		case "one_time":
			return "One time";
		case "persistent":
			return "Persistent";
		case "valid_until":
			return "Valid until";
		default:
			return mode.replace("_", " ");
	}
}

function instructionStatusLabel(status: string) {
	switch (status) {
		case "pending":
			return "Pending";
		case "delivered":
			return "Delivered";
		case "acknowledged":
			return "Acknowledged";
		case "completed":
			return "Completed";
		case "canceled":
			return "Canceled";
		case "expired":
			return "Expired";
		default:
			return status;
	}
}

function DetailRow({
	label,
	value,
}: {
	label: string;
	value: React.ReactNode;
}) {
	return (
		<div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 text-sm">
			<div className="text-slate-500">{label}</div>
			<div className="min-w-0 break-words text-slate-800">{value || "-"}</div>
		</div>
	);
}

function CompactDetail({
	label,
	value,
}: {
	label: string;
	value: React.ReactNode;
}) {
	const displayValue =
		value === null || value === undefined || value === "" ? "-" : value;
	return (
		<div className="min-w-0 rounded-md border border-indigo-100/70 bg-white/70 px-2.5 py-2">
			<div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
				{label}
			</div>
			<div
				className="mt-0.5 min-w-0 truncate text-xs text-slate-700"
				title={typeof value === "string" ? value : undefined}
			>
				{displayValue}
			</div>
		</div>
	);
}

function StringList({
	items,
	emptyText,
}: {
	items?: string[];
	emptyText: string;
}) {
	if (!items?.length) {
		return <p className="text-sm italic text-slate-400">{emptyText}</p>;
	}
	return (
		<ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
			{items.map((item, index) => (
				<li key={`${item}-${index}`}>{item}</li>
			))}
		</ul>
	);
}

function formatEstimatedCost(cost: number): string {
	if (cost === 0) return "$0";
	if (cost < 0.0001) return `<$0.0001`;
	return `$${cost.toFixed(4)}`;
}

function ModelUsageTile({
	label,
	audit,
}: {
	label: string;
	audit: ModelUsageAudit;
}) {
	const thoughtsTokens =
		audit.usage.thoughtsTokens ??
		Math.max(
			0,
			audit.usage.totalTokens -
				audit.usage.inputTokens -
				audit.usage.outputTokens,
		);

	return (
		<div className="rounded-md border border-violet-100 bg-violet-50/60 p-2.5">
			<div className="flex items-center justify-between gap-2">
				<span className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
					{label}
				</span>
				<span className="text-xs font-semibold text-violet-900">
					{formatEstimatedCost(audit.estimatedCostUsd.total)}
				</span>
			</div>
			<div
				className="mt-1 truncate font-mono text-[11px] text-slate-700"
				title={audit.model}
			>
				{audit.model}
			</div>
			<div className="mt-1 text-[10px] text-slate-500">
				{audit.usage.inputTokens.toLocaleString()} in ·{" "}
				{audit.usage.outputTokens.toLocaleString()} response ·{" "}
				{thoughtsTokens.toLocaleString()} reasoning ·{" "}
				{audit.usage.totalTokens.toLocaleString()} total
			</div>
			<div className="mt-1 text-[10px] text-slate-500">
				${audit.pricing.inputUsdPerMillionTokens}/M input · $
				{audit.pricing.outputUsdPerMillionTokens}/M response + reasoning
				{" · "}
				<a
					href={audit.pricing.source}
					target="_blank"
					rel="noreferrer"
					className="text-indigo-600 hover:underline"
				>
					rates as of {audit.pricing.asOf}
				</a>
			</div>
		</div>
	);
}

export function CallerDetailsPage({
	companyId,
	callerId,
	onBack,
}: {
	companyId: string;
	callerId: string;
	onBack: () => void;
}) {
	const [caller, setCaller] = useState<Caller | null>(null);
	const [updates, setUpdates] = useState<
		(CallerMemoryUpdate & { id: string })[]
	>([]);
	const [instructions, setInstructions] = useState<CallerInstruction[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [retryingAction, setRetryingAction] = useState<string | null>(null);
	const [isInstructionDialogOpen, setIsInstructionDialogOpen] = useState(false);
	const [instructionDraft, setInstructionDraft] = useState("");
	const [instructionMode, setInstructionMode] =
		useState<CallerInstructionMode>("one_time");
	const [validTill, setValidTill] = useState("");
	const [isSubmittingInstruction, setIsSubmittingInstruction] = useState(false);
	const [cancelingInstructionId, setCancelingInstructionId] = useState<
		string | null
	>(null);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isDeletingCaller, setIsDeletingCaller] = useState(false);

	useEffect(() => {
		if (!companyId || !callerId) return;

		const callerRef = doc(db, "companies", companyId, "callers", callerId);
		const unsubCaller = onSnapshot(callerRef, (snap) => {
			if (snap.exists()) {
				const data = snap.data() as Caller;
				setCaller({
					...data,
					memory: normalizeCallerMemory(data.memory),
				});
			} else {
				setCaller(null);
			}
			setIsLoading(false);
		});

		const updatesRef = collection(callerRef, "memoryUpdates");
		const q = query(updatesRef, orderBy("createdAt", "desc"));
		const unsubUpdates = onSnapshot(q, (snap) => {
			const fetched: (CallerMemoryUpdate & { id: string })[] = [];
			snap.forEach((doc) => {
				fetched.push({
					...(doc.data() as CallerMemoryUpdate),
					id: doc.id,
				});
			});
			setUpdates(fetched);
		});

		const instructionsRef = collection(callerRef, "instructions");
		const unsubInstructions = onSnapshot(instructionsRef, (snap) => {
			setInstructions(
				snap.docs
					.map((doc) => ({
						...(doc.data() as CallerInstruction),
						id: doc.id,
					}))
					.sort(
						(a, b) =>
							Number(b.active) - Number(a.active) ||
							(b.priority || 0) - (a.priority || 0) ||
							dateMs(b.createdAt) - dateMs(a.createdAt),
					),
			);
		});

		return () => {
			unsubCaller();
			unsubUpdates();
			unsubInstructions();
		};
	}, [companyId, callerId]);

	const createInstruction = async () => {
		if (!instructionDraft.trim()) return;
		setIsSubmittingInstruction(true);
		try {
			await httpsCallable(
				functions,
				"createCallerInstruction",
			)({
				companyId,
				callerId,
				instruction: instructionDraft,
				mode: instructionMode,
				validTill:
					instructionMode === "valid_until" && validTill
						? new Date(validTill).toISOString()
						: null,
			});
			toast.success("Caller instruction created.");
			setIsInstructionDialogOpen(false);
			setInstructionDraft("");
			setInstructionMode("one_time");
			setValidTill("");
		} catch (error) {
			toast.error(`Failed to create instruction: ${errorMessage(error)}`);
		} finally {
			setIsSubmittingInstruction(false);
		}
	};

	const cancelInstruction = async (instructionId: string) => {
		setCancelingInstructionId(instructionId);
		try {
			await httpsCallable(
				functions,
				"cancelCallerInstruction",
			)({ companyId, callerId, instructionId });
			toast.success("Instruction canceled.");
		} catch (error) {
			toast.error(`Failed to cancel instruction: ${errorMessage(error)}`);
		} finally {
			setCancelingInstructionId(null);
		}
	};

	const deleteCaller = async () => {
		setIsDeletingCaller(true);
		try {
			await deleteCallerCascade({ companyId, callerId });
			toast.success(`Caller ${callerId} was deleted.`);
			setIsDeleteDialogOpen(false);
			onBack();
		} catch (error) {
			toast.error(`Failed to delete caller: ${errorMessage(error)}`);
		} finally {
			setIsDeletingCaller(false);
		}
	};

	if (isLoading)
		return (
			<div className="p-8 text-center text-slate-500">
				Loading caller memory...
			</div>
		);
	if (!caller)
		return (
			<div className="p-8 text-center text-slate-500">
				Caller not found in database.
			</div>
		);

	return (
		<div className="space-y-6">
			<AlertDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Permanently delete this caller?</AlertDialogTitle>
						<AlertDialogDescription>
							This removes caller {callerId} and every document nested beneath
							it, including memory updates and instructions. This action cannot
							be undone and will be recorded in the audit log.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeletingCaller}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(event) => {
								event.preventDefault();
								void deleteCaller();
							}}
							disabled={isDeletingCaller}
							className="bg-red-600 text-white hover:bg-red-700"
						>
							{isDeletingCaller ? "Deleting..." : "Delete caller"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<div className="flex items-center justify-between gap-4">
				<div className="flex items-center space-x-4">
					<Button variant="ghost" size="icon" onClick={onBack}>
						<ArrowLeft className="w-4 h-4" />
					</Button>
					<div>
						<h1 className="text-3xl font-bold tracking-tight">
							{caller.name || "Unknown Caller"}
						</h1>
						<p className="text-muted-foreground">
							{caller.phoneNumber} • {caller.callCount} calls • Generation{" "}
							{caller.memoryGeneration}
						</p>
					</div>
				</div>
				<Button
					variant="destructive"
					onClick={() => setIsDeleteDialogOpen(true)}
					className="gap-2"
				>
					<Trash2 className="h-4 w-4" />
					Delete Caller
				</Button>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Caller document and current snapshot */}
				<Card className="border-indigo-100 bg-indigo-50/40 shadow-sm h-fit">
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-indigo-900">
							<BrainCircuit className="w-5 h-5 text-indigo-600" />
							Caller Details & Current Memory
						</CardTitle>
						<CardDescription>
							Complete current caller document and consolidated memory snapshot.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-6">
						<section className="space-y-3">
							<h3 className="text-xs font-bold uppercase tracking-wider text-indigo-800">
								Profile
							</h3>
							<DetailRow
								label="Caller ID"
								value={<span className="font-mono text-xs">{callerId}</span>}
							/>
							<DetailRow
								label="Company ID"
								value={<span className="font-mono text-xs">{companyId}</span>}
							/>
							<DetailRow label="Name" value={caller.name} />
							<DetailRow label="Phone" value={caller.phoneNumber} />
							<DetailRow label="Email" value={caller.email} />
							<DetailRow
								label="Notes"
								value={
									caller.notes ? (
										<span className="whitespace-pre-wrap">{caller.notes}</span>
									) : (
										"-"
									)
								}
							/>
							<DetailRow
								label="Tags"
								value={
									caller.tags?.length ? (
										<div className="flex flex-wrap gap-1.5">
											{caller.tags.map((tag) => (
												<Badge key={tag} variant="secondary">
													{tag}
												</Badge>
											))}
										</div>
									) : (
										"-"
									)
								}
							/>
						</section>

						<Separator />

						<section className="space-y-2">
							<h3 className="text-xs font-bold uppercase tracking-wider text-indigo-800">
								Language & Call History
							</h3>
							<div className="grid grid-cols-2 gap-2 xl:grid-cols-5">
								<CompactDetail
									label="Language"
									value={caller.preferredLanguage || caller.memory?.language}
								/>
								<CompactDetail label="Calls" value={caller.callCount} />
								<CompactDetail
									label="First Call"
									value={formatDate(caller.firstCallAt)}
								/>
								<CompactDetail
									label="Last Call"
									value={formatDate(caller.lastCallAt)}
								/>
								<CompactDetail
									label="Updated"
									value={formatDate(caller.updatedAt)}
								/>
							</div>
						</section>

						<Separator />

						<section className="space-y-4">
							<div>
								<h3 className="text-xs font-bold uppercase tracking-wider text-indigo-800">
									Current Memory Snapshot
								</h3>
								<p className="mt-1 text-xs text-slate-500">
									The stable memory currently available to the agent. Version{" "}
									{caller.memory?.version || 0}.
								</p>
							</div>
							{caller.memory ? (
								<>
									<div className="grid grid-cols-2 gap-3 rounded-md border border-indigo-100 bg-white p-3">
										<DetailRow
											label="Language"
											value={caller.memory.language}
										/>
										<DetailRow label="Version" value={caller.memory.version} />
										<DetailRow
											label="Updated"
											value={formatDate(caller.memory.updatedAt)}
										/>
										<DetailRow
											label="Last call ID"
											value={
												<span className="font-mono text-xs">
													{caller.memory.lastProcessedCallId}
												</span>
											}
										/>
									</div>
									{caller.memory.summary && (
										<div>
											<h4 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-indigo-800">
												Summary
											</h4>
											<div className="p-3 bg-white border border-indigo-100 shadow-sm rounded-md text-sm text-slate-800 whitespace-pre-wrap">
												{caller.memory.summary}
											</div>
										</div>
									)}
									<div>
										<h4 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-indigo-800">
											Facts
										</h4>
										<StringList
											items={caller.memory.facts}
											emptyText="No facts recorded."
										/>
									</div>
									<div>
										<h4 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-indigo-800">
											Preferences
										</h4>
										<StringList
											items={caller.memory.preferences}
											emptyText="No preferences recorded."
										/>
									</div>
									<div className="rounded-md border border-indigo-100 bg-white p-3">
										<div className="mb-3 flex items-center justify-between gap-3">
											<div>
												<h4 className="text-[11px] font-bold uppercase tracking-wider text-indigo-800">
													Standing Caller Instructions
												</h4>
												<p className="mt-1 text-xs text-slate-500">
													Per-caller instructions delivered to agents during
													future calls.
												</p>
											</div>
											<Button
												variant="outline"
												size="sm"
												className="h-8 shrink-0"
												onClick={() => setIsInstructionDialogOpen(true)}
											>
												<Plus className="mr-2 h-4 w-4" />
												Add Instruction
											</Button>
										</div>
										{instructions.length === 0 ? (
											<p className="text-sm italic text-slate-400">
												No standing instructions for this caller.
											</p>
										) : (
											<div className="space-y-3">
												{instructions.map((instruction) => (
													<div
														key={instruction.id}
														className="rounded-md border border-slate-200 bg-slate-50/70 p-3 text-sm"
													>
														<div className="mb-2 flex flex-wrap items-center gap-2">
															<Badge
																variant={
																	instruction.active ? "default" : "secondary"
																}
															>
																{instructionStatusLabel(instruction.status)}
															</Badge>
															<Badge variant="outline">
																{instructionModeLabel(instruction.mode)}
															</Badge>
															{instruction.lastDeliveryOutcome && (
																<Badge variant="outline">
																	Outcome: {instruction.lastDeliveryOutcome}
																</Badge>
															)}
														</div>
														<p className="whitespace-pre-wrap text-slate-800">
															{instruction.instruction}
														</p>
														<div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500 xl:grid-cols-3">
															<div>
																Created: {formatDate(instruction.createdAt)}
															</div>
															<div>
																Valid till: {formatDate(instruction.validTill)}
															</div>
															<div>
																Delivered: {formatDate(instruction.deliveredAt)}
															</div>
															<div>
																Acknowledged:{" "}
																{formatDate(instruction.acknowledgedAt)}
															</div>
															<div>
																Completed: {formatDate(instruction.completedAt)}
															</div>
															<div>
																Attempts:{" "}
																{instruction.deliveryAttemptCount || 0}
															</div>
														</div>
														{instruction.lastDeliveryNote && (
															<div className="mt-2 rounded bg-white px-2 py-1 text-xs text-slate-600">
																{instruction.lastDeliveryNote}
															</div>
														)}
														{instruction.active && instruction.id && (
															<Button
																variant="ghost"
																size="sm"
																className="mt-2 h-7 px-2 text-xs text-red-600"
																disabled={
																	cancelingInstructionId === instruction.id
																}
																onClick={() =>
																	cancelInstruction(instruction.id as string)
																}
															>
																{cancelingInstructionId === instruction.id
																	? "Canceling..."
																	: "Cancel instruction"}
															</Button>
														)}
													</div>
												))}
											</div>
										)}
									</div>
									<div>
										<h4 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-indigo-800">
											Recent Interactions
										</h4>
										{caller.memory.recentInteractions?.length ? (
											<div className="space-y-3">
												{caller.memory.recentInteractions.map(
													(interaction, index) => (
														<div
															key={`${interaction.callId}-${index}`}
															className="rounded-md border bg-white p-3 text-sm"
														>
															<div className="flex items-start justify-between gap-3">
																<div className="font-medium text-slate-800">
																	{interaction.topic || "Untitled interaction"}
																</div>
																<div className="shrink-0 text-xs text-slate-400">
																	{formatDate(interaction.callCreatedAt)}
																</div>
															</div>
															<div className="mt-1 text-xs font-mono text-slate-400">
																{interaction.callId}
															</div>
															{interaction.outcome && (
																<div className="mt-2 text-slate-700">
																	<span className="font-medium">Outcome:</span>{" "}
																	{interaction.outcome}
																</div>
															)}
															{interaction.summary && (
																<div className="mt-1 whitespace-pre-wrap text-slate-600">
																	{interaction.summary}
																</div>
															)}
														</div>
													),
												)}
											</div>
										) : (
											<p className="text-sm italic text-slate-400">
												No recent interactions recorded.
											</p>
										)}
									</div>
								</>
							) : (
								<div className="text-sm text-slate-500 italic p-4 text-center border rounded-md border-dashed border-indigo-200">
									No memory snapshot exists. Wait for a meaningful call or
									trigger a backfill.
								</div>
							)}

							<div className="space-y-2 rounded-md border border-indigo-100 bg-indigo-50/60 p-3">
								<div>
									<h4 className="text-[11px] font-bold uppercase tracking-wider text-indigo-800">
										Latest Update Processing
									</h4>
									<p className="mt-1 text-xs text-slate-500">
										Processing state of the latest memory update. Pending or
										failed updates do not change the snapshot above.
									</p>
								</div>
								<div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
									<CompactDetail
										label="Generation"
										value={caller.memoryGeneration}
									/>
									<CompactDetail
										label="Status"
										value={
											caller.latestMemoryUpdateStatus ? (
												<Badge
													variant="outline"
													className="h-5 px-1.5 text-[10px]"
												>
													{caller.latestMemoryUpdateStatus}
												</Badge>
											) : (
												"-"
											)
										}
									/>
									<CompactDetail
										label="Latest At"
										value={formatDate(caller.latestMemoryUpdateAt)}
									/>
									{caller.latestMemoryUpdateErrorCode && (
										<CompactDetail
											label="Error"
											value={
												<span className="text-red-600">
													{caller.latestMemoryUpdateErrorCode}
												</span>
											}
										/>
									)}
									{caller.memoryClearedAt && (
										<CompactDetail
											label="Cleared At"
											value={formatDate(caller.memoryClearedAt)}
										/>
									)}
									{caller.memoryClearedBy && (
										<CompactDetail
											label="Cleared By"
											value={caller.memoryClearedBy}
										/>
									)}
								</div>
							</div>
						</section>

						<Separator />

						<details className="rounded-md border bg-white">
							<summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
								Raw Caller Document
							</summary>
							<pre className="max-h-96 overflow-auto border-t bg-slate-950 p-3 text-[11px] text-slate-100">
								{JSON.stringify(caller, null, 2)}
							</pre>
						</details>
					</CardContent>
				</Card>

				<Dialog
					open={isInstructionDialogOpen}
					onOpenChange={setIsInstructionDialogOpen}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Add Standing Caller Instruction</DialogTitle>
						</DialogHeader>
						<div className="space-y-4 py-2">
							<div className="space-y-2">
								<Label htmlFor="caller-instruction">Instruction</Label>
								<Textarea
									id="caller-instruction"
									value={instructionDraft}
									onChange={(event) => setInstructionDraft(event.target.value)}
									maxLength={1000}
									placeholder="Next time this caller calls, mention that their appointment moved to Thursday."
								/>
							</div>
							<div className="space-y-2">
								<Label>Mode</Label>
								<Select
									value={instructionMode}
									onValueChange={(value: CallerInstructionMode) =>
										setInstructionMode(value)
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="one_time">One time</SelectItem>
										<SelectItem value="persistent">Persistent</SelectItem>
										<SelectItem value="valid_until">Valid until</SelectItem>
									</SelectContent>
								</Select>
							</div>
							{instructionMode === "valid_until" && (
								<div className="space-y-2">
									<Label htmlFor="caller-instruction-valid-till">
										Valid till
									</Label>
									<Input
										id="caller-instruction-valid-till"
										type="datetime-local"
										value={validTill}
										onChange={(event) => setValidTill(event.target.value)}
									/>
								</div>
							)}
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsInstructionDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button
								onClick={createInstruction}
								disabled={isSubmittingInstruction || !instructionDraft.trim()}
							>
								{isSubmittingInstruction ? "Creating..." : "Create"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Update History Debugger */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<History className="w-5 h-5" />
							Memory Update Diagnostics
						</CardTitle>
						<CardDescription>
							Complete event log for {updates.length} update attempts.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-4 max-h-[800px] overflow-y-auto pr-2">
							{updates.map((update) => (
								<div
									key={update.id}
									className="p-4 border rounded-md bg-white shadow-sm"
								>
									<div className="flex items-start justify-between mb-2">
										<div className="flex flex-col space-y-1.5">
											<div className="flex items-center space-x-2">
												<Badge
													variant={
														update.status === "completed"
															? "default"
															: update.status === "skipped"
																? "secondary"
																: update.status === "failed"
																	? "destructive"
																	: "outline"
													}
												>
													{update.status}
												</Badge>
												<span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
													{update.source === "call"
														? `Call: ${update.callId}`
														: update.source}
												</span>
											</div>
											<span className="text-[11px] text-slate-400 font-mono">
												Update ID: {update.id}
											</span>
										</div>
										<div className="flex flex-col items-end">
											<span className="text-xs text-slate-500 font-medium">
												{new Date(update.createdAt ?? "").toLocaleString()}
											</span>
											{update.basedOnVersion !== undefined &&
												update.resultingVersion !== undefined && (
													<span className="text-[10px] text-indigo-500 mt-1">
														v{update.basedOnVersion} → v
														{update.resultingVersion}
													</span>
												)}
											{(update.status === "failed" ||
												update.status === "skipped" ||
												update.status === "processing") && (
												<div className="mt-2 flex max-w-[220px] flex-col items-stretch gap-1.5">
													<Button
														variant="outline"
														size="sm"
														className="h-7 px-2 text-[10px]"
														disabled={retryingAction !== null}
														title="Reuse the existing extraction delta and rerun only memory consolidation."
														onClick={async () => {
															const actionId = `${update.id}:consolidation`;
															setRetryingAction(actionId);
															try {
																const retryCallable = httpsCallable(
																	functions,
																	"retryCallerMemoryUpdate",
																);
																await retryCallable({
																	companyId,
																	callerId,
																	updateId: update.id,
																});
															} catch (err: unknown) {
																const message =
																	err instanceof Error
																		? err.message
																		: String(err);
																alert(
																	`Failed to retry consolidation: ${message}`,
																);
															} finally {
																setRetryingAction(null);
															}
														}}
													>
														{retryingAction === `${update.id}:consolidation`
															? "Retrying..."
															: "Retry consolidation only"}
													</Button>
													{(update.source === "call" ||
														update.source === "backfill") && (
														<Button
															variant="outline"
															size="sm"
															className="h-7 px-2 text-[10px] text-indigo-700"
															disabled={retryingAction !== null}
															title="Run extraction again from the saved call content, replace the delta, then run consolidation."
															onClick={async () => {
																const actionId = `${update.id}:extraction`;
																setRetryingAction(actionId);
																try {
																	const retryCallable = httpsCallable(
																		functions,
																		"retryCallerMemoryExtraction",
																	);
																	await retryCallable({
																		companyId,
																		callerId,
																		updateId: update.id,
																	});
																} catch (err: unknown) {
																	const message =
																		err instanceof Error
																			? err.message
																			: String(err);
																	alert(
																		`Failed to retry extraction: ${message}`,
																	);
																} finally {
																	setRetryingAction(null);
																}
															}}
														>
															{retryingAction === `${update.id}:extraction`
																? "Re-extracting..."
																: "Re-extract and consolidate"}
														</Button>
													)}
												</div>
											)}
										</div>
									</div>

									{update.skipReason && (
										<div className="text-sm text-slate-600 italic bg-slate-50 p-2 rounded mt-2">
											Skipped: {update.skipReason}
										</div>
									)}

									{update.errorMessage && (
										<div className="text-sm text-red-600 flex items-start gap-1.5 bg-red-50 p-2 rounded mt-2">
											<AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
											<div>
												<div className="font-bold">{update.errorCode}</div>
												<div>{update.errorMessage}</div>
											</div>
										</div>
									)}

									{update.modelUsage && (
										<div className="mt-4 text-xs">
											<div className="mb-1 font-semibold uppercase tracking-wider text-slate-700 text-[10px]">
												Model Usage
											</div>
											<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
												{update.modelUsage.extraction && (
													<ModelUsageTile
														label="Extraction"
														audit={update.modelUsage.extraction}
													/>
												)}
												{update.modelUsage.consolidation && (
													<ModelUsageTile
														label="Consolidation"
														audit={update.modelUsage.consolidation}
													/>
												)}
											</div>
										</div>
									)}

									{update.modelInput?.extraction && (
										<details className="mt-4 rounded border border-amber-100 bg-amber-50/50 text-xs">
											<summary className="cursor-pointer px-2.5 py-1.5 font-semibold uppercase tracking-wider text-slate-700 text-[10px]">
												Extraction Model Input
											</summary>
											<div className="max-h-80 space-y-3 overflow-y-auto border-t border-amber-100 p-2.5">
												<div>
													<div className="mb-1 font-semibold text-slate-500">
														{update.modelInput.extraction.contentType.replaceAll(
															"_",
															" ",
														)}
													</div>
													<pre className="whitespace-pre-wrap font-mono text-[11px] text-slate-700">
														{update.modelInput.extraction.content}
													</pre>
												</div>
												{update.modelInput.extraction.serviceTagsContext && (
													<div>
														<div className="mb-1 font-semibold text-slate-500">
															Service tags context
														</div>
														<pre className="whitespace-pre-wrap font-mono text-[11px] text-slate-700">
															{update.modelInput.extraction.serviceTagsContext}
														</pre>
													</div>
												)}
											</div>
										</details>
									)}

									{update.delta && (
										<details className="mt-4 rounded border bg-slate-50 text-xs">
											<summary className="cursor-pointer px-2.5 py-1.5 font-semibold uppercase tracking-wider text-slate-700 text-[10px]">
												Extraction Delta
											</summary>
											<div className="max-h-48 overflow-y-auto whitespace-pre-wrap border-t p-2.5 font-mono text-[11px]">
												{JSON.stringify(update.delta, null, 2)}
											</div>
										</details>
									)}

									{update.modelInput?.consolidation && (
										<details className="mt-4 rounded border border-cyan-100 bg-cyan-50/50 text-xs">
											<summary className="cursor-pointer px-2.5 py-1.5 font-semibold uppercase tracking-wider text-slate-700 text-[10px]">
												Consolidation Model Input
											</summary>
											<div className="max-h-80 overflow-y-auto whitespace-pre-wrap border-t border-cyan-100 p-2.5 font-mono text-[11px]">
												{JSON.stringify(
													update.modelInput.consolidation,
													null,
													2,
												)}
											</div>
										</details>
									)}

									{update.proposedChanges && (
										<details className="mt-4 rounded border border-indigo-100 bg-indigo-50/50 text-xs">
											<summary className="cursor-pointer px-2.5 py-1.5 font-semibold uppercase tracking-wider text-slate-700 text-[10px]">
												Proposed Semantic Changes
											</summary>
											<div className="max-h-48 overflow-y-auto whitespace-pre-wrap border-t border-indigo-100 p-2.5 font-mono text-[11px]">
												{JSON.stringify(update.proposedChanges, null, 2)}
											</div>
										</details>
									)}

									{update.resultingMemory && (
										<details className="mt-4 rounded border border-emerald-100 bg-emerald-50/50 text-xs">
											<summary className="cursor-pointer px-2.5 py-1.5 font-semibold uppercase tracking-wider text-slate-700 text-[10px]">
												Resulting Consolidated Memory
											</summary>
											<div className="max-h-64 overflow-y-auto whitespace-pre-wrap border-t border-emerald-100 p-2.5 font-mono text-[11px]">
												{JSON.stringify(update.resultingMemory, null, 2)}
											</div>
										</details>
									)}
								</div>
							))}
							{updates.length === 0 && (
								<div className="text-sm text-slate-500 italic p-6 text-center border border-dashed rounded-md">
									No update history recorded yet.
								</div>
							)}
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
